import { PillButton } from './PillButton';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NotificationView } from '@aralere/shared-types';
import { useAuth } from '../AuthProvider.js';
import { api } from '../api-client.js';

// In-app notification bell + toast (spec §12 SYSTEM, 08 Q25). Rendered inside AppShell's header for
// BOTH surfaces (admin + client). The server (/api/notifications) resolves which notifications this
// principal sees (audience + the per-user receipt lifecycle) and returns ALL active-to-user rows
// (newest first), each tagged `viewed` (this user opened the bell with it active) and `type`
// (broadcast | invoice | payment-reminder), plus `unreadCount` (active-to-user with no firstViewedAt).
//
//   • Bell    — soft brand tint whenever ANYTHING is uncleared (active rows present); a small filled
//               count circle for NEW/unviewed only (unreadCount). Opening the bell = "viewed":
//               POST mark-viewed stamps firstViewedAt set-once (starts the broadcast 72h clocks +
//               clears the count, synced across devices). Each row has a × → POST dismiss (removes it
//               for this user immediately).
//   • Dropdown— drops from / collapses into the bell, nested under the header; shows ~5 rows then
//               scrolls. Invoice/payment rows carry a billing soft-glow (.notification-billing).
//   • Toast   — ONE at a time, anchored under the bell (NOT a header-overlapping fixed overlay). The
//               most-recent unviewed pops on login; mid-session arrivals queue and show one by one.
//               A toast's auto-dismiss / × is PURELY VISUAL — it does NOT mark-viewed or dismiss the
//               notification and does NOT start the 72h clock (only opening the bell does).

const POLL_MS = 60_000;
const TOAST_MS = 8_000;

const bellIcon = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M10 2.5a4.5 4.5 0 0 0-4.5 4.5c0 3-1 4.2-1.6 4.9-.2.2-.3.5-.2.8.1.3.4.5.7.5h11.2c.3 0 .6-.2.7-.5.1-.3 0-.6-.2-.8-.6-.7-1.6-1.9-1.6-4.9A4.5 4.5 0 0 0 10 2.5Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path d="M8.2 16.2a1.9 1.9 0 0 0 3.6 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const dismissIcon = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M3.5 3.5l7 7m0-7l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

function isBilling(n: NotificationView): boolean {
  return n.type === 'invoice' || n.type === 'payment-reminder';
}

// 08 Q33 glow palette (wired s37, s30 item 8): the optional `category` maps to a themeable glow
// class (tokens live beside --notification-billing-glow in the portal stylesheet; 'billing' reuses
// the EXISTING .notification-billing class/token). FALLBACK: a row without a category keeps the
// original type-based billing soft glow, so invoice/payment rows glow correctly even before their
// Stage-4 writer stamps a category. Presentation only — never affects routing or lifecycle.
const CATEGORY_CLASS: Record<string, string> = {
  'action-required': 'notification-cat-action-required',
  error: 'notification-cat-error',
  'service-assigned': 'notification-cat-service-assigned',
  'service-removed': 'notification-cat-service-removed',
  'service-disabled': 'notification-cat-service-disabled',
  'payment-partial': 'notification-cat-payment-partial',
  'payment-received': 'notification-cat-payment-received',
  billing: 'notification-billing',
};

function glowClass(n: NotificationView): string {
  if (n.category && CATEGORY_CLASS[n.category]) return CATEGORY_CLASS[n.category];
  return isBilling(n) ? 'notification-billing' : '';
}

// Types that surface ONLY in the bell — they bump the unread count and bell tint but never pop as a
// toast banner (owner decision, s36): the s30 item-4 bulk-cascade notices, plus the slice-3
// retainer-low notice (04 #20 / Q42 — assignment-negative, like the loss notices). Broadcasts,
// billing reminders, and service-assigned keep the §12 toast behavior.
const BELL_ONLY_TYPES = new Set<string>(['service-disabled', 'service-reenabled', 'service-removed', 'retainer-low']);

function toasts(n: NotificationView): boolean {
  return !BELL_ONLY_TYPES.has(n.type ?? 'broadcast');
}

function formatPostDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function NotificationCenter(): JSX.Element | null {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationView[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<NotificationView | null>(null); // ONE at a time

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const knownIds = useRef<Set<string>>(new Set()); // ids already observed this session (won't re-toast)
  const primed = useRef(false); // first load seeds knownIds (no backlog toast storm)
  const toastQueue = useRef<NotificationView[]>([]); // pending toasts, shown one by one
  const toastTimer = useRef<number | null>(null);

  // Deep-link: close the bell and navigate in-app. Relative SPA paths only (set server-side).
  const openLink = useCallback(
    (linkTo: string | null | undefined): void => {
      if (!linkTo) return;
      setOpen(false);
      navigate(linkTo);
    },
    [navigate]
  );

  // Show a toast if none is up, else queue it (one-at-a-time).
  const enqueueToast = useCallback((n: NotificationView): void => {
    setToast((cur) => {
      if (cur) {
        if (cur.id !== n.id && !toastQueue.current.some((q) => q.id === n.id)) {
          toastQueue.current.push(n);
        }
        return cur;
      }
      return n;
    });
  }, []);

  // Dismiss the CURRENT toast (auto/×) and surface the next queued one. Purely visual — never
  // touches mark-viewed/dismiss or the 72h clock.
  const dismissToast = useCallback((): void => {
    if (toastTimer.current) {
      window.clearTimeout(toastTimer.current);
      toastTimer.current = null;
    }
    setToast(toastQueue.current.shift() ?? null);
  }, []);

  const load = useCallback(async (): Promise<void> => {
    try {
      const data = await api.notifications.list();
      setItems(data.notifications);
      setUnread(data.unreadCount);

      const newlyObserved: NotificationView[] = [];
      for (const n of data.notifications) {
        if (!knownIds.current.has(n.id)) {
          knownIds.current.add(n.id);
          newlyObserved.push(n);
        }
      }
      if (!primed.current) {
        // First load: toast ONLY the most-recent unviewed (notifications are newest-first);
        // bell-only types never toast.
        primed.current = true;
        const mostRecentUnviewed = data.notifications.find((n) => !n.viewed && toasts(n));
        if (mostRecentUnviewed) enqueueToast(mostRecentUnviewed);
      } else {
        // Mid-session: enqueue each NEW unviewed arrival (queue handles one-at-a-time);
        // bell-only types never toast.
        for (const n of newlyObserved) if (!n.viewed && toasts(n)) enqueueToast(n);
      }
    } catch {
      /* best-effort — a polling hiccup must never disrupt the shell */
    }
  }, [enqueueToast]);

  // Poll while signed in, plus refetch when the tab regains focus.
  useEffect(() => {
    if (!user) return;
    void load();
    const id = window.setInterval(() => void load(), POLL_MS);
    const onFocus = (): void => void load();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, load]);

  // Auto-dismiss the current toast after a few seconds (or on × / unmount). Visual only.
  useEffect(() => {
    if (!toast) return;
    toastTimer.current = window.setTimeout(() => dismissToast(), TOAST_MS);
    return () => {
      if (toastTimer.current) {
        window.clearTimeout(toastTimer.current);
        toastTimer.current = null;
      }
    };
  }, [toast, dismissToast]);

  // Close the dropdown on outside-click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggleOpen = useCallback((): void => {
    setOpen((wasOpen) => {
      const next = !wasOpen;
      if (next && unread > 0) {
        // Opening the bell = viewed (set-once). Clear the count + mark rows viewed optimistically;
        // persist server-side (best-effort, synced cross-device). This starts the broadcast 72h clocks.
        setUnread(0);
        setItems((prev) => prev.map((n) => ({ ...n, viewed: true })));
        void api.notifications.markViewed().then(
          (r) => setUnread(r.unreadCount),
          () => {
            /* if the stamp fails, the next poll re-derives the count from the server */
          },
        );
      }
      return next;
    });
  }, [unread]);

  // × on a bell row → close this notification for this user (immediate removal).
  const dismissRow = useCallback(
    (id: string): void => {
      setItems((prev) => prev.filter((n) => n.id !== id));
      void api.notifications.dismiss(id).then(
        (r) => setUnread(r.unreadCount),
        () => {
          void load(); // on failure, refetch to resync
        },
      );
    },
    [load],
  );

  // "Clear all" (owner, s104): close every visible row for this user in one tap. Optimistic
  // like dismissRow; there is no bulk endpoint (this slice stays frontend-only), so it fans
  // out the per-row dismiss and resyncs on any failure.
  const dismissAll = useCallback((): void => {
    const ids = items.map((n) => n.id);
    if (ids.length === 0) return;
    setItems([]);
    setUnread(0);
    void Promise.all(ids.map((id) => api.notifications.dismiss(id))).catch(() => {
      void load();
    });
  }, [items, load]);

  if (!user) return null;

  const hasActive = items.length > 0;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        aria-label={unread > 0 ? `Notifications, ${unread} new` : 'Notifications'}
        aria-expanded={open}
        className={
          'relative flex items-center justify-center h-9 w-9 rounded transition hover:bg-[rgb(var(--edge-rgb)/0.05)] ' +
          (hasActive ? 'notification-bell-on text-[var(--bell-active-text)]' : 'text-[var(--color-text)]')
        }
      >
        {bellIcon}
        {unread > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--color-primary)] text-[var(--btn-primary-text)] text-[10px] font-semibold leading-[16px] text-center">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {/* Dropdown — drops from / collapses into the bell, nested under the header. s103a fix: on
          MOBILE the bell sits top-LEFT (the s103 header swap), so a bell-anchored right-0 panel
          would hang off-screen left — below md it re-anchors as a full-width layer just under the
          header (still never overlapping it, per the s37 rule); md+ is byte-identical to before. */}
      {open ? (
        <div
          role="dialog"
          aria-label="Notifications"
          className="fixed left-3 right-3 top-16 mt-2 w-auto max-w-none md:absolute md:left-auto md:right-0 md:top-auto md:w-80 md:max-w-[calc(100vw-2rem)] rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-raised)] shadow-lg overflow-hidden z-30"
        >
          <div className="px-4 py-2.5 border-b border-[rgb(var(--edge-rgb)/0.10)] flex items-center gap-3">
            <span className="font-display text-sm tracking-tight">Notifications</span>
            {items.length > 0 ? (
              <PillButton size="xs" onClick={dismissAll}>
                Clear all
              </PillButton>
            ) : null}
          </div>
          {items.length > 0 ? (
            // ~5 rows tall, then scroll.
            <ul className="max-h-[19rem] overflow-y-auto divide-y divide-[rgb(var(--edge-rgb)/0.05)]">
              {items.map((n) => (
                <li
                  key={n.id}
                  className={
                    'px-4 py-3 flex items-start gap-3 ' +
                    (glowClass(n) ? glowClass(n) + ' ' : '') +
                    (n.linkTo ? 'hover:bg-[rgb(var(--edge-rgb)/0.05)]' : '')
                  }
                >
                  <div
                    className={'min-w-0 flex-1' + (n.linkTo ? ' cursor-pointer' : '')}
                    onClick={n.linkTo ? () => { dismissRow(n.id); openLink(n.linkTo); } : undefined}
                    role={n.linkTo ? 'link' : undefined}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm font-medium text-[var(--color-text)]">{n.headline}</span>
                      <span className="text-[11px] text-[var(--color-muted)] whitespace-nowrap">
                        {formatPostDate(n.postDate)}
                      </span>
                    </div>
                    {n.body ? (
                      <p className="mt-0.5 text-xs text-[var(--color-muted)] leading-snug">{n.body}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => dismissRow(n.id)}
                    aria-label="Dismiss"
                    className="shrink-0 -mr-1 -mt-0.5 h-6 w-6 rounded flex items-center justify-center text-[var(--color-muted)] hover:bg-[rgb(var(--edge-rgb)/0.05)] transition"
                  >
                    {dismissIcon}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-6 text-sm text-[var(--color-muted)] text-center">You're all caught up.</p>
          )}
        </div>
      ) : null}

      {/* Toast — one at a time, anchored under the bell on md+ (only while the dropdown is closed); below md it uses the same under-the-header full-width anchor as the dropdown (s103a). */}
      {!open && toast ? (
        <div
          role="status"
          className={
            // A glowing toast drops shadow-lg: the Tailwind utility's box-shadow sits in a later CSS
            // layer and would override the glow ring (s37 toast-glow fix); the glow reads as elevation.
            'fixed left-3 right-3 top-16 mt-2 w-auto max-w-none md:absolute md:left-auto md:right-0 md:top-auto md:w-80 md:max-w-[calc(100vw-2rem)] rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-raised)] px-4 py-3 flex items-start gap-3 z-40 ' +
            (glowClass(toast) ? glowClass(toast) : 'shadow-lg')
          }
        >
          <div
            className={'flex-1 min-w-0' + (toast.linkTo ? ' cursor-pointer' : '')}
            onClick={
              toast.linkTo
                ? () => {
                    const to = toast.linkTo;
                    dismissRow(toast.id); // mark the underlying notification dismissed (server + bell list), not just visual
                    dismissToast();
                    if (to) navigate(to);
                  }
                : undefined
            }
            role={toast.linkTo ? 'link' : undefined}
          >
            <p className="text-sm font-medium text-[var(--color-text)]">{toast.headline}</p>
            {toast.body ? (
              <p className="mt-0.5 text-xs text-[var(--color-muted)] leading-snug">{toast.body}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={dismissToast}
            aria-label="Dismiss"
            className="shrink-0 -mr-1 -mt-0.5 h-6 w-6 rounded flex items-center justify-center text-[var(--color-muted)] hover:bg-[rgb(var(--edge-rgb)/0.05)] transition"
          >
            {dismissIcon}
          </button>
        </div>
      ) : null}
    </div>
  );
}
